import type * as THREE from 'three';
import { AIRFRAME_DAMAGE, DAMAGE } from '../config/tuning';
import type { Checksum, Checksummable } from '../core/Checksum';
import { hashUnit } from '../core/Rng';

/**
 * Airframe battle damage (SPEC §3.6.4, §3.8, and the M7 note).
 *
 * A single integrity number is not enough. The M7 implementation note is specific: *"asymmetric
 * control authority after a control surface loss must actually make the aircraft fly crooked,
 * requiring held opposite input"* — so damage is tracked per system, and which system is hit
 * depends on where the warhead was relative to the airframe.
 *
 * That is the difference between a damage model the player *reads* and one they *fly*. A wounded
 * drone that needs a boot of opposite stick all the way home is a story; a wounded drone with a
 * smaller number on a gauge is a status effect.
 *
 * Everything here is 0 (undamaged) to 1 (destroyed). Nothing allocates.
 */

export const DamageSystem = {
  LeftWing: 'left-wing',
  RightWing: 'right-wing',
  Tail: 'tail',
  Engine: 'engine',
  Fuel: 'fuel',
  Structure: 'structure',
} as const;
export type DamageSystemId = (typeof DamageSystem)[keyof typeof DamageSystem];

export class DamageState implements Checksummable {
  /** Per-system damage, 0–1. */
  leftWing = 0;
  rightWing = 0;
  tail = 0;
  engine = 0;
  fuel = 0;
  structure = 0;

  /** Total airframe integrity, 1 = pristine. Derived, and what the HUD and flight model read. */
  integrity = 1;

  reset(): void {
    this.leftWing = 0;
    this.rightWing = 0;
    this.tail = 0;
    this.engine = 0;
    this.fuel = 0;
    this.structure = 0;
    this.integrity = 1;
  }

  /**
   * Net roll bias from asymmetric wing damage, rad/s².
   *
   * Positive rolls right. A damaged left wing loses lift and drops, so the aircraft rolls *left*
   * — the sign here is the difference between a bug report and a flying lesson.
   */
  get rollBiasRadS2(): number {
    return (this.rightWing - this.leftWing) * AIRFRAME_DAMAGE.asymmetricRollRadS2;
  }

  /** Yaw bias from tail damage, rad/s². Signed by which side took it, via the same asymmetry. */
  get yawBiasRadS2(): number {
    return (this.rightWing - this.leftWing) * AIRFRAME_DAMAGE.asymmetricYawRadS2 * this.tail;
  }

  /** Roll authority remaining, 0–1. Both ailerons contribute; losing either costs control. */
  get rollAuthorityFactor(): number {
    const worst = Math.max(this.leftWing, this.rightWing);
    return 1 - AIRFRAME_DAMAGE.surfaceAuthorityLoss * worst;
  }

  /** Pitch and yaw authority remaining, 0–1, from the tail. */
  get tailAuthorityFactor(): number {
    return 1 - AIRFRAME_DAMAGE.surfaceAuthorityLoss * this.tail;
  }

  /** Thrust remaining, 0–1. */
  get thrustFactor(): number {
    return 1 - (1 - AIRFRAME_DAMAGE.engineThrustFloor) * this.engine;
  }

  /** Fuel lost per second through a punctured tank, kg/s. */
  get fuelLeakKgS(): number {
    return AIRFRAME_DAMAGE.fuelLeakKgS * this.fuel;
  }

  get engineFailed(): boolean {
    return 1 - this.integrity >= DAMAGE.engineLossThreshold;
  }

  get structuralFailure(): boolean {
    return 1 - this.integrity >= DAMAGE.structuralThreshold;
  }

  /** Worst-damaged system, for the debrief and the damage smoke. */
  worstSystem(): DamageSystemId {
    let worst: DamageSystemId = DamageSystem.Structure;
    let value = this.structure;
    const check = (system: DamageSystemId, amount: number): void => {
      if (amount > value) {
        value = amount;
        worst = system;
      }
    };
    check(DamageSystem.LeftWing, this.leftWing);
    check(DamageSystem.RightWing, this.rightWing);
    check(DamageSystem.Tail, this.tail);
    check(DamageSystem.Engine, this.engine);
    check(DamageSystem.Fuel, this.fuel);
    return worst;
  }

  checksum(into: Checksum): void {
    into.addFloat(this.leftWing);
    into.addFloat(this.rightWing);
    into.addFloat(this.tail);
    into.addFloat(this.engine);
    into.addFloat(this.fuel);
    into.addFloat(this.structure);
    into.addFloat(this.integrity);
  }
}

/**
 * Apply a detonation to the airframe, distributing it across systems by where the blast came from.
 *
 * A warhead that goes off to the left takes the left wing; one that goes off behind takes the
 * tail and the engine. Small hits spread as general structural damage — it takes a meaningful
 * detonation nearby to sever a control surface, which is what keeps the asymmetric case rare
 * enough to stay memorable.
 *
 * `relativeM` is the blast position in the *airframe's* frame: +x right, +y up, −z forward.
 * `eventId` seeds the deterministic spread, so a replay damages the same systems.
 */
export function applyBlastDamage(
  state: DamageState,
  relativeM: THREE.Vector3,
  amount: number,
  eventId: number,
): void {
  if (amount <= 0) return;

  if (amount < AIRFRAME_DAMAGE.surfaceHitThreshold) {
    // A shallow near miss peppers the airframe rather than removing anything from it.
    state.structure = clamp01(state.structure + amount);
    recomputeIntegrity(state);
    return;
  }

  const lateral = relativeM.x;
  const longitudinal = -relativeM.z; // positive is ahead of the airframe
  const magnitude = Math.max(relativeM.length(), 1e-4);

  // Fractions of the blast each system absorbs, from the geometry.
  const sideBias = Math.abs(lateral) / magnitude;
  const rearBias = Math.max(0, -longitudinal) / magnitude;

  // A little deterministic variation so two identical geometries do not always break the same
  // component — drawn from a hash rather than a stream, so a replay reproduces it exactly.
  const jitter = 0.8 + 0.4 * hashUnit(eventId, 0x0a11);

  const toWing = amount * sideBias * 0.75 * jitter;
  const toTail = amount * rearBias * 0.5 * jitter;
  const toEngine = amount * rearBias * 0.4 * jitter;
  const toFuel = amount * (1 - rearBias) * 0.25 * jitter;
  const remainder = Math.max(0, amount - toWing - toTail - toEngine - toFuel);

  if (lateral >= 0) state.rightWing = clamp01(state.rightWing + toWing);
  else state.leftWing = clamp01(state.leftWing + toWing);

  state.tail = clamp01(state.tail + toTail);
  state.engine = clamp01(state.engine + toEngine);
  state.fuel = clamp01(state.fuel + toFuel);
  state.structure = clamp01(state.structure + remainder);

  recomputeIntegrity(state);
}

/**
 * Apply damage with no geometry behind it — an over-g pull, a collision, a scripted event.
 *
 * Spread evenly across every system so that repeated damage can actually destroy the aircraft.
 * Concentrating it in one system caps the harm at that system's weighting, which turns a damage
 * model into an invulnerability bug the first time something hits the aircraft more than a few
 * times.
 */
export function spreadDamage(state: DamageState, fraction: number): void {
  if (fraction <= 0) return;
  const share = fraction;
  state.leftWing = clamp01(state.leftWing + share);
  state.rightWing = clamp01(state.rightWing + share);
  state.tail = clamp01(state.tail + share);
  state.engine = clamp01(state.engine + share);
  state.fuel = clamp01(state.fuel + share);
  state.structure = clamp01(state.structure + share);
  recomputeIntegrity(state);
}

/**
 * Wear the airframe by over-stressing it — over-g, overspeed buffet (SPEC §3.2.4).
 *
 * Distinct from `spreadDamage` only in intent, and identical in effect because the weights sum to
 * one: `attrition(f)` removes exactly `f` from integrity. That equivalence is the point. Attrition
 * used to be written straight into `FlightState.integrity`, which gave that number two owners —
 * this model and the flight model — and they overwrote each other. Routing it here means damage
 * has a single source of truth whatever inflicted it.
 */
export function applyAttrition(state: DamageState, fraction: number): void {
  spreadDamage(state, fraction);
}

/** Total loss: hitting the ground, or exceeding the failure g. Nothing survives. */
export function destroyAirframe(state: DamageState): void {
  state.leftWing = 1;
  state.rightWing = 1;
  state.tail = 1;
  state.engine = 1;
  state.fuel = 1;
  state.structure = 1;
  state.integrity = 0;
}

/**
 * Integrity from the per-system damage.
 *
 * Weighted, not averaged: losing the structure or the engine matters more to whether the aircraft
 * is still an aircraft than losing a fuel cell does. The weights sum to one so a total loss
 * everywhere gives exactly zero.
 */
export function recomputeIntegrity(state: DamageState): void {
  const weighted =
    state.leftWing * 0.16 +
    state.rightWing * 0.16 +
    state.tail * 0.14 +
    state.engine * 0.2 +
    state.fuel * 0.1 +
    state.structure * 0.24;
  state.integrity = clamp01(1 - weighted);
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
